/**
 * Display formatters (DESIGN_SYSTEM §3, NFR-I18N-1). The only allowed way to render money,
 * dates, phones and empty values. Business timezone is Asia/Dhaka (BR-2).
 */
export const APP_TIMEZONE = 'Asia/Dhaka';
/** Shown for missing values (styled with `--text-subtle` in the UI). */
export const EMPTY_VALUE = '—';
/** Salary masked until revealed (FR-SAL-8). */
export const MASKED_MONEY = '৳ ••••••';
/** Normalised BD mobile number as stored (SRS §9). */
export const PHONE_REGEX = /^01[3-9]\d{8}$/;
const MONTHS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
];
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const taka = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });
/** `211365` → `৳ 2,11,365` (South-Asian grouping, whole taka). Accepts DB numeric strings. */
export function formatMoney(value) {
    if (value === null || value === undefined || value === '')
        return EMPTY_VALUE;
    const amount = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(amount))
        throw new RangeError(`Not a money amount: ${String(value)}`);
    const rounded = Math.round(amount);
    const formatted = `৳ ${taka.format(Math.abs(rounded))}`;
    return rounded < 0 ? `-${formatted}` : formatted;
}
const dhakaParts = new Intl.DateTimeFormat('en-US', {
    timeZone: APP_TIMEZONE,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hourCycle: 'h23',
});
function toDhaka(instant) {
    if (Number.isNaN(instant.getTime()))
        throw new RangeError('Invalid date');
    const parts = {};
    for (const p of dhakaParts.formatToParts(instant))
        parts[p.type] = p.value;
    return {
        year: Number(parts['year']),
        month: Number(parts['month']),
        day: Number(parts['day']),
        hour: Number(parts['hour']),
        minute: Number(parts['minute']),
    };
}
const pad2 = (n) => String(n).padStart(2, '0');
function monthName(month) {
    const name = MONTHS[month - 1];
    if (name === undefined)
        throw new RangeError(`Invalid month: ${String(month)}`);
    return name;
}
/** Calendar date "today" in Asia/Dhaka as `YYYY-MM-DD` (BR-1). */
export function todayInDhaka(now = new Date()) {
    const { year, month, day } = toDhaka(now);
    return `${String(year)}-${pad2(month)}-${pad2(day)}`;
}
/**
 * `2018-09-16` → `16 Sep 2018`. Calendar dates (`YYYY-MM-DD`) are shown as-is; instants
 * (`Date` or ISO timestamp strings) are converted to Asia/Dhaka first.
 */
export function formatDate(value) {
    if (value === null || value === undefined || value === '')
        return EMPTY_VALUE;
    if (typeof value === 'string') {
        const m = ISO_DATE.exec(value);
        if (m)
            return `${String(Number(m[3]))} ${monthName(Number(m[2]))} ${String(m[1])}`;
    }
    const { year, month, day } = toDhaka(typeof value === 'string' ? new Date(value) : value);
    return `${String(day)} ${monthName(month)} ${String(year)}`;
}
/** A calendar month `2025-07` → `Jul 2025`. */
export function formatMonth(ym) {
    return `${monthName(Number(ym.slice(5, 7)))} ${ym.slice(0, 4)}`;
}
/** Instant → `14 Oct 2026, 2:33 pm` in Asia/Dhaka. */
export function formatDateTime(value) {
    if (value === null || value === undefined || value === '')
        return EMPTY_VALUE;
    const { year, month, day, hour, minute } = toDhaka(typeof value === 'string' ? new Date(value) : value);
    return `${String(day)} ${monthName(month)} ${String(year)}, ${clock(hour, minute)}`;
}
/** Instant → `9:52 am` in Asia/Dhaka (punch times, FR-ATT-3). */
export function formatTime(value) {
    if (value === null || value === undefined || value === '')
        return EMPTY_VALUE;
    const { hour, minute } = toDhaka(typeof value === 'string' ? new Date(value) : value);
    return clock(hour, minute);
}
/** `14, 33` → `2:33 pm`. */
function clock(hour, minute) {
    const h12 = hour % 12 === 0 ? 12 : hour % 12;
    return `${String(h12)}:${pad2(minute)} ${hour < 12 ? 'am' : 'pm'}`;
}
const relative = new Intl.RelativeTimeFormat('en', { numeric: 'always' });
const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
/**
 * Relative time for audit entries and requests: `3 hours ago` within 7 days, otherwise the
 * absolute date-time (DESIGN_SYSTEM §3).
 */
export function formatRelative(value, now = new Date()) {
    if (value === null || value === undefined || value === '')
        return EMPTY_VALUE;
    const instant = typeof value === 'string' ? new Date(value) : value;
    const diff = instant.getTime() - now.getTime();
    const abs = Math.abs(diff);
    if (Number.isNaN(abs))
        throw new RangeError('Invalid date');
    if (abs >= 7 * DAY)
        return formatDateTime(instant);
    if (abs < MINUTE)
        return 'just now';
    if (abs < HOUR)
        return relative.format(Math.trunc(diff / MINUTE), 'minute');
    if (abs < DAY)
        return relative.format(Math.trunc(diff / HOUR), 'hour');
    return relative.format(Math.trunc(diff / DAY), 'day');
}
/** `1031` → `#1031` in chips, `1031` in tables. */
export function formatEmpCode(code, options = {}) {
    if (code === null || code === undefined)
        return EMPTY_VALUE;
    return options.chip ? `#${String(code)}` : String(code);
}
/**
 * Normalises a Bangladeshi mobile number to `01XXXXXXXXX` (SRS §9): strips spaces, dashes,
 * dots, brackets and the `+880` / `880` / `00880` prefix, and restores a leading zero lost in
 * spreadsheets. Returns `null` when empty or not a valid BD mobile number.
 */
export function normalizePhone(raw) {
    if (raw === null || raw === undefined)
        return null;
    let digits = String(raw).replace(/[\s\-().]/g, '');
    if (digits.startsWith('+'))
        digits = digits.slice(1);
    else if (digits.startsWith('00'))
        digits = digits.slice(2);
    if (digits.startsWith('880'))
        digits = digits.slice(3);
    if (/^1[3-9]\d{8}$/.test(digits))
        digits = `0${digits}`;
    return PHONE_REGEX.test(digits) ? digits : null;
}
/** `01787669192` → `017 8766 9192`. Values that are not normalised phones are returned as-is. */
export function formatPhone(value) {
    if (value === null || value === undefined || value === '')
        return EMPTY_VALUE;
    if (!PHONE_REGEX.test(value))
        return value;
    return `${value.slice(0, 3)} ${value.slice(3, 7)} ${value.slice(7)}`;
}
/** `211365` → `2,11,365`: the grouped digits without `৳`, for MoneyInput (prefix shown apart). */
export function formatMoneyAmount(value) {
    if (value === null || value === undefined)
        return '';
    if (!Number.isFinite(value))
        throw new RangeError(`Not a money amount: ${String(value)}`);
    return taka.format(Math.round(value));
}
/**
 * Parses what a user typed into a MoneyInput: grouping commas, spaces and `৳` are ignored.
 * Returns `null` for an empty field and `NaN` for anything that is not a whole taka amount.
 */
export function parseMoneyInput(raw) {
    const cleaned = raw.replace(/[\s,৳]/g, '');
    if (cleaned === '')
        return null;
    return /^\d+$/.test(cleaned) ? Number(cleaned) : Number.NaN;
}
const weekdayName = new Intl.DateTimeFormat('en-US', { weekday: 'long', timeZone: 'UTC' });
function isoParts(iso) {
    const m = ISO_DATE.exec(iso);
    if (!m)
        throw new RangeError(`Not an ISO date: ${iso}`);
    return { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
}
/** `2026-10-14` → `14/10/2026`, the typed format of the DatePicker. */
export function formatDateInput(iso) {
    if (iso === null || iso === undefined || iso === '')
        return '';
    const { year, month, day } = isoParts(iso);
    return `${pad2(day)}/${pad2(month)}/${String(year)}`;
}
/**
 * Parses a typed `DD/MM/YYYY` (also `D/M/YYYY`, `-` or `.` separators) into `YYYY-MM-DD`.
 * Returns `null` when the text is not a real calendar date.
 */
export function parseDateInput(raw) {
    const m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(raw.trim());
    if (!m)
        return null;
    const [day, month, year] = [Number(m[1]), Number(m[2]), Number(m[3])];
    const date = new Date(Date.UTC(year, month - 1, day));
    if (date.getUTCFullYear() !== year ||
        date.getUTCMonth() !== month - 1 ||
        date.getUTCDate() !== day) {
        return null;
    }
    return `${String(year)}-${pad2(month)}-${pad2(day)}`;
}
/** `2026-10-14` → `Wednesday`. */
export function formatWeekday(iso) {
    const { year, month, day } = isoParts(iso);
    return weekdayName.format(new Date(Date.UTC(year, month - 1, day)));
}
/** `2026-10-14` → `2026-11-01`: the "1st of next month" quick pick for effective dates. */
export function firstOfNextMonth(iso) {
    const { year, month } = isoParts(iso);
    return month === 12 ? `${String(year + 1)}-01-01` : `${String(year)}-${pad2(month + 1)}-01`;
}
/** `5242880` → `5 MB`, `1536` → `1.5 KB` (file sizes on FileSlot). */
export function formatFileSize(bytes) {
    if (!Number.isFinite(bytes) || bytes < 0)
        throw new RangeError(`Not a file size: ${String(bytes)}`);
    if (bytes < 1024)
        return `${String(bytes)} B`;
    const exp = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), 3);
    const size = Math.round((bytes / 1024 ** exp) * 10) / 10;
    return `${String(size)} ${'KMG'.charAt(exp - 1)}B`;
}
//# sourceMappingURL=format.js.map